import React from 'react';
import {
  Card,
  Typography,
  Box,
  Avatar,
  SxProps,
  Stack,
  Fade,
  Divider,
  CardActionArea,
} from '@mui/material';

import { TP_COLORS } from '../../../../constants';
import { getShortName } from '../../../../helper';
import {
  getLessonDeliveredCount,
  getRandomTutorBio,
  getTotalExperienceYears,
} from '../../Tutor';
import { ITutor } from '../../../../store/V2/TutorSlice';

const cardStyles: SxProps = {
  borderRadius: '20px',
  boxShadow: '0px 14px 40px 0px #6E9DB22E',
};

const boxStyles: SxProps = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const avatarStyles: SxProps = {
  width: '72px',
  height: '72px',
  fontSize: '36px',
  border: '2px solid #F8FAFC',
};

const trialTypoStyles: SxProps = {
  borderRadius: '35px',
  padding: '11px 24px',
  fontSize: '14px',
  fontWeight: '600',
  border: '1px solid #94A3B8',
};

interface TutorCardProps {
  tutorData: ITutor;
  handleSelect: (tutorId: string) => void;
}

const TutorCard: React.FC<TutorCardProps> = ({ tutorData, handleSelect }) => {
  const experienceYears = getTotalExperienceYears(
    tutorData.profile?.experiences || []
  );

  const lessonDelivered = getLessonDeliveredCount(tutorData);
  const rating = (Number(tutorData.tutorDetails?.rating) || 4).toFixed(1);
  const languages = tutorData.profile?.languages
    ?.map((language) => {
      return `${language.language} (${language.proficiency})`;
    })
    .join(', ');

  return (
    <Card sx={cardStyles}>
      <CardActionArea
        onClick={() => handleSelect(tutorData._id)}
        sx={{ padding: '20px' }}
      >
        <Stack spacing={1}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box sx={boxStyles}>
              <Avatar
                src={tutorData.avatar}
                alt={tutorData.firstname}
                sx={avatarStyles}
              />

              <Typography fontSize="18px" fontWeight="600">
                {getShortName(tutorData.firstname, tutorData.lastname)}
              </Typography>
              <img
                src={`${process.env.PUBLIC_URL}/images/tutorProfile/verified-icon.png`}
                alt="verified-icon"
                width={22}
                height={22}
              />
            </Box>
            <Fade in={Boolean(tutorData.rate)}>
              <Typography fontSize="18px" fontWeight="600">
                {tutorData.rate?.amount}
                <span style={{ fontSize: '16px', marginLeft: '2px' }}>
                  {tutorData.rate?.currency}/hr
                </span>
              </Typography>
            </Fade>
          </Box>

          <Stack
            sx={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '10px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '3px',
              }}
            >
              <img
                src={`${process.env.PUBLIC_URL}/images/tutorProfile/star-yellow-icon.png`}
                alt="star-yellow-icon"
              />
              <Typography
                fontSize="14px"
                fontWeight="600"
                color={TP_COLORS.gray.darken}
              >
                {rating}/5
              </Typography>
              <Typography fontSize="14px" color="#94A3B8">
                ({tutorData.tutorDetails?.noOfReviews || 50} Reviews)
              </Typography>
            </Box>
            <Box display="flex" alignItems="center">
              <Typography
                fontSize="14px"
                fontWeight="600"
                color={TP_COLORS.gray.darken}
              >
                {experienceYears}
              </Typography>
              <Typography fontSize="14px" color="#94A3B8" marginLeft="3px">
                Experience
              </Typography>
            </Box>
          </Stack>

          <Divider flexItem variant="middle" sx={{ borderStyle: 'dashed' }} />

          <Box padding="10px">
            <Typography
              fontSize="14px"
              color="#94A3B8"
              lineHeight="18px"
              className="ellipsis-text three-lines"
            >
              {getRandomTutorBio(
                tutorData._id,
                getShortName(tutorData.firstname, tutorData.lastname, false),
                experienceYears,
                lessonDelivered
              )}
            </Typography>
          </Box>

          <Box
            display="flex"
            padding="8px"
            visibility={
              languages && languages.length > 0 ? 'visible' : 'hidden'
            }
          >
            <Typography
              fontSize="14px"
              fontWeight="600"
              color={TP_COLORS.gray.darken}
            >
              Speaks
            </Typography>
            <Typography
              fontSize="14px"
              color="#94A3B8"
              marginLeft="3px"
              className="ellipsis-text one-line"
            >
              {languages}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              fontSize="14px"
              padding="10px"
              className="ellipsis-text one-line"
            >
              <span style={{ fontWeight: '600', color: TP_COLORS.green.light }}>
                {lessonDelivered}+{' '}
              </span>
              Lesson delivered
            </Typography>

            <Typography sx={trialTypoStyles}>Free Trial</Typography>
          </Box>
        </Stack>
      </CardActionArea>
    </Card>
  );
};

export default TutorCard;
